import { useEffect, useState, useCallback } from 'react';
import { getLastLocation, saveShowPopupGeoloc, saveLastLocation } from '../../localStorageAPI';
import { WebViewChannel, isWebView, sendMessageToNativeApp } from '../../utils/webview';

export type Config = {
    enableHighAccuracy: boolean;
    maximumAge: number;
    timeout: number;
};

export interface UseLocationOutput {
    position: GeolocationPosition;
    latLng: {
        lat: number;
        lng: number;
    };
    error: GeolocationPositionError;
}

interface GeolocationCoordinates {
    readonly accuracy: number;
    readonly altitude: number | null;
    readonly altitudeAccuracy: number | null;
    readonly heading: number | null;
    readonly latitude: number;
    readonly longitude: number;
    readonly speed: number | null;
}

interface GeolocationPosition {
    readonly coords: GeolocationCoordinates;
    readonly timestamp: number;
}
interface GeolocationPositionError {
    readonly code: number;
    readonly message: string;
}

export type GeolocationCallback = (data: GeolocationPosition) => void;

export const defaultState: GeolocationPosition = {
    coords: {
        accuracy: null,
        altitude: null,
        altitudeAccuracy: null,
        heading: null,
        latitude: null,
        longitude: null,
        speed: null,
    },
    timestamp: null,
};

const defaultConfig: Config = {
    enableHighAccuracy: true,
    maximumAge: 15000,
    timeout: 10000,
};

const getGeolocationPositionFromLastPosition = (): GeolocationPosition => {
    const lastLocation = getLastLocation();
    return {
        coords: {
            latitude: lastLocation?.latLng?.lat,
            longitude: lastLocation?.latLng?.lng,
            accuracy: null,
            altitude: null,
            altitudeAccuracy: null,
            heading: null,
            speed: null,
        },
        timestamp: new Date().getTime(),
    };
};

// Errors
// 1	PERMISSION_DENIED	L’acquisition de la position échoue car la page n'a pas l'autorisation de le faire.
// 2	POSITION_UNAVAILABLE	L’acquisition de la localisation échoue car au moins une source donnant la position a retourné une erreur interne.
// 3	TIMEOUT	Le temps alloué pour obtenir la position défini par PositionOptions.timeout est écoulé avant que l'information soit obtenu.
// 4	GEOLOCATION_NOT_SUPPORTED	L'objet navigator.geolocation n'est pas disponible.

export const useGeolocation = (config: Config = defaultConfig, callback?: GeolocationCallback): UseLocationOutput => {
    const [position, setPosition] = useState<GeolocationPosition>(getGeolocationPositionFromLastPosition());
    const [error, setError] = useState<GeolocationPositionError>(null);

    const onSuccess = useCallback(
        (geolocationPosition: GeolocationPosition): void => {
            if (position) {
                saveShowPopupGeoloc(true);
                setPosition(geolocationPosition);
                if (callback) {
                    callback(geolocationPosition);
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [callback],
    );

    const onError = (err): void => {
        setError(err);
    };

    useEffect(() => {
        let watcherID = null;
        let geolocation = null;

        if (!isWebView()) {
            const lastLocation: UseLocationOutput = getLastLocation();
            if (lastLocation?.latLng?.lat) {
                const savedPosition: GeolocationPosition = getGeolocationPositionFromLastPosition();
                setPosition(savedPosition);
            } else {
                geolocation = navigator.geolocation;
                if (!geolocation) {
                    setError({ code: 4, message: 'Geolocation is not supported by your browser' });
                } else {
                    watcherID = geolocation.watchPosition(onSuccess, onError, config);
                }
            }
        }

        return () => {
            if (geolocation && watcherID) {
                // console.log('cleaning watch');
                geolocation.clearWatch(watcherID);
            }
        };
    }, [config, onSuccess]);

    const isWebview = isWebView();
    const lastLocation: UseLocationOutput = getLastLocation();
    const res: UseLocationOutput = isWebview
        ? lastLocation
        : {
              position,
              latLng: {
                  lat: +position?.coords?.latitude?.toFixed(7) || null,
                  lng: +position?.coords?.longitude?.toFixed(7) || null,
              },
              error,
          };

    if (!isWebview && position.timestamp && lastLocation?.position?.timestamp !== position.timestamp) {
        saveLastLocation(res);
    }

    if (!res) {
        if (isWebview) {
            // In case we don't have the current position for some reason, ask it again to app
            sendMessageToNativeApp(WebViewChannel.ASK_LOCATION, {});
        }
        console.log('UseLocation was NULL -->', { res });
    }
    return res;
};

export default useGeolocation;
